<script setup lang="ts">
import BaseDropdown from "@/components/general/Dropdown/Dropdown.vue";
import { useGlobalStore, useCustomerStore } from "@/stores";

interface DateFieldProps {
    hasError?: {
        day?: boolean;
        month?: boolean;
        year?: boolean;
    };
    onValidate?: () => void;
    onGetDeals?: (field: string) => void;
}

const dobKeys = {
    day: "dobDate",
    month: "dobMonth",
    year: "dobYear",
} as const;

const props = defineProps<DateFieldProps>();
const emit = defineEmits<{
    (e: "blur", field: "date" | "month" | "year"): void;
}>();

const global = useGlobalStore();
const customer = useCustomerStore();

const fields = [
    {
        id: "DOBDate",
        key: "day",
        placeholder: "Day",
        ariaLabel: "Day of birth",
        options: Array.from({ length: 31 }, (_, i) => {
            const value = global.leftPad(i + 1);
            return { label: value, value };
        }),
        blurField: "date" as const,
        changeType: "dob day",
    },
    {
        id: "DOBMonth",
        key: "month",
        placeholder: "Month",
        ariaLabel: "Month of birth",
        options: Array.from({ length: 12 }, (_, i) => {
            const value = global.leftPad(i + 1);
            return { label: value, value };
        }),
        blurField: "month" as const,
        changeType: "dob month",
    },
    {
        id: "DOBYear",
        key: "year",
        placeholder: "Year",
        ariaLabel: "Year of birth",
        options: global.years.map((y) => ({
            label: String(y),
            value: String(y),
        })),
        blurField: "year" as const,
        changeType: "age",
    },
] as const;

// ------------------ Methods ------------------
function handleChange(type: string) {
    props.onValidate?.();
    props.onGetDeals?.(type);
}
</script>

<template>
    <div class="date-dropdown-group">
        <div v-for="field in fields" :key="field.id" class="dob-field">
            <BaseDropdown
                :id="field.id"
                v-model="customer[dobKeys[field.key]]"
                :name="field.id"
                :options="field.options"
                :placeholder="field.placeholder"
                :aria-label="field.ariaLabel"
                :has-error="props.hasError?.[field.key]"
                :full-width="true"
                @blur="emit('blur', field.blurField)"
                @change="handleChange(field.changeType)"
            />
        </div>
    </div>
</template>

<style scoped>
.date-dropdown-group {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
}

.dob-field {
    flex: 1 1 calc(33.333% - 0.75rem);
    min-width: 120px;
}

@media (width <= 600px) {
    .dob-field {
        flex: 1 1 calc(50% - 0.75rem);
    }

    .dob-field:last-child {
        flex-basis: 100%;
    }
}
</style>
